import { useActiveLocation } from '../../hooks/useActiveLocation';
import api from '../../services/api';
import type { Location } from '../../types/Location';
import type { Order } from '../../types/Order';
import type { Restaurant } from '../../types/Restaurant';
import type { Table } from '../../types/Table';
import type { DateRange } from './range';
import type { Stats } from './stats';
import { Remote, useRemote } from './useRemote';

const LIVE_REFRESH_MS = 15_000;
const RANGE_REFRESH_MS = 60_000;

export interface SetupStep {
    id: 'location' | 'menu' | 'tables' | 'order';
    done: boolean;
}

export interface HomeData {
    locations: Remote<Location[]>;
    /** GET /stats for the chosen range; idle for roles that only see the live list. */
    stats: Remote<Stats>;
    live: Remote<Order[]>;
    /** null while the checklist is still being worked out. */
    setup: SetupStep[] | null;
    /** The location the title refers to; null means all locations. */
    current: Location | null;
}

const fetchOrders = (params: Record<string, string | undefined>) =>
    api.get<Order[]>('/orders', { params }).then((r) => r.data);

/** The venue's IANA zone, which decides what "today" is. Falls back to the browser's zone if it cannot be read. */
export const useVenueZone = (): { zone: string | null; restaurantId: string | null } => {
    const { restaurantId } = useActiveLocation();
    const venue = useRemote(restaurantId ? `venue:${restaurantId}` : null, () =>
        api.get<Restaurant>(`/restaurants/${restaurantId}`).then((r) => r.data.timezone)
    );
    const zone = venue.state === 'loading' ? null : (venue.data ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
    return { zone, restaurantId };
};

export const useHomeData = (range: DateRange | null, opts: { overview: boolean; setup: boolean }): HomeData => {
    const { restaurantId, locationId, locations: options, isAdmin } = useActiveLocation();
    const scoped = restaurantId !== null && (locationId !== null || !isAdmin);
    const where = locationId ?? 'all';

    const locations = useRemote(restaurantId ? `loc:${restaurantId}` : null, () =>
        api.get<Location[]>(`/restaurants/${restaurantId}/locations`).then((r) => r.data)
    );

    const live = useRemote(
        scoped ? `live:${where}` : null,
        () => fetchOrders({ status: 'active', locationId: locationId ?? undefined }),
        LIVE_REFRESH_MS
    );

    const statsRemote = useRemote(
        scoped && opts.overview && range ? `stats:${restaurantId}:${where}:${range.from}:${range.to}` : null,
        () =>
            api
                .get<Stats>('/stats', {
                    params: { from: range?.from, to: range?.to, locationId: locationId ?? undefined, restaurantId: isAdmin ? restaurantId : undefined },
                })
                .then((r) => r.data),
        RANGE_REFRESH_MS
    );
    // Until the time zone is known the range is unknown: the cards are loading, not empty.
    const stats: Remote<Stats> = scoped && opts.overview && !range ? { ...statsRemote, state: 'loading' } : statsRemote;

    const ids = options.map((o) => o.id);
    const tables = useRemote(opts.setup && ids.length > 0 ? `tables:${ids.join(',')}` : null, () =>
        Promise.all(ids.map((id) => api.get<Table[]>(`/locations/${id}/tables`).then((r) => r.data.length))).then((counts) =>
            counts.reduce((a, b) => a + b, 0)
        )
    );

    const recentOrder = (stats.data?.totals.orders ?? 0) > 0 || (live.data?.length ?? 0) > 0;
    const hasLocation = (locations.data?.length ?? 0) > 0;
    const hasMenu = (locations.data ?? []).some((l) => Boolean(l.activeMenuId ?? l.activeMenu?.id));
    const hasTable = (tables.data ?? 0) > 0;

    // The full order history is only fetched when nothing recent is on screen and the other steps are done.
    const askHistory = opts.setup && !recentOrder && hasLocation && hasMenu && hasTable && live.state === 'ready' && stats.state !== 'loading';
    const history = useRemote(askHistory ? `history:${where}` : null, () =>
        fetchOrders({ locationId: locationId ?? undefined }).then((orders) => orders.length > 0)
    );

    const settled = locations.state !== 'loading' && stats.state !== 'loading' && tables.state !== 'loading' && live.state !== 'loading' && history.state !== 'loading';
    const setup: SetupStep[] | null =
        opts.setup && settled && locations.state === 'ready' && tables.state === 'ready'
            ? [
                  { id: 'location', done: hasLocation },
                  { id: 'menu', done: hasMenu },
                  { id: 'tables', done: hasTable },
                  { id: 'order', done: recentOrder || history.data === true },
              ]
            : null;

    const current = (locations.data ?? []).find((l) => l.id === locationId) ?? null;
    return { locations, stats, live, setup, current };
};
